import React, { useState } from "react";
import { db } from "../../../firebaseConfig";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import styles from "./Timetable.module.css";

const DEPARTMENTS = [
    { label: "Computer Science", value: "CS" }
];

const PROGRAMS = [
    { label: "BS Computer Science", value: "BCS" },
    { label: "BS Software Engineering", value: "BSE" }
];

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];

export default function AddTimetable() {
    const [department, setDepartment] = useState(DEPARTMENTS[0].value);
    const [program, setProgram] = useState(PROGRAMS[0].value);
    const [semester, setSemester] = useState("1");
    const [section, setSection] = useState("1");

    const [slots, setSlots] = useState([
        {
            label: "1st",
            days: DAYS.reduce((acc, d) => {
                acc[d] = { subject: "", room: "", teacher: "" };
                return acc;
            }, {}),
        },
        {
            label: "2nd",
            days: DAYS.reduce((acc, d) => {
                acc[d] = { subject: "", room: "", teacher: "" };
                return acc;
            }, {}),
        },
        {
            label: "3rd",
            days: DAYS.reduce((acc, d) => {
                acc[d] = { subject: "", room: "", teacher: "" };
                return acc;
            }, {}),
        },
        {
            label: "4th",
            days: DAYS.reduce((acc, d) => {
                acc[d] = { subject: "", room: "", teacher: "" };
                return acc;
            }, {}),
        }
    ]);

    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");

    const handleCellChange = (slotIndex, day, field, value) => {
        setSlots((prev) => {
            const copy = JSON.parse(JSON.stringify(prev));
            copy[slotIndex].days[day][field] = value;
            return copy;
        });
    };

    const addSlot = () => {
        const nextLabel = `${slots.length + 1}th`;
        const newSlot = {
            label: nextLabel,
            days: DAYS.reduce((acc, d) => {
                acc[d] = { subject: "", room: "", teacher: "" };
                return acc;
            }, {}),
        };
        setSlots((s) => [...s, newSlot]);
    };

    const removeSlot = (index) => {
        setSlots((s) => s.filter((_, i) => i !== index));
    };

    const validate = () => {
        if (!department || !program || !semester || !section) {
            setMessage("Please fill filters (department/program/semester/section).");
            return false;
        }
        return true;
    };

    const handleSave = async () => {
        setMessage("");
        if (!validate()) return;

        setLoading(true);
        try {
            // Document ID format: CS_BSC_1_1
            const docId = `${department}_${program}_${semester}_${section}`;
            const docRef = doc(db, "timetables", docId);

            const payload = {
                department,
                program,
                semester,
                section,
                slots,
                updatedAt: serverTimestamp(),
            };

            await setDoc(docRef, payload, { merge: true });

            setMessage("Timetable saved successfully.");
        } catch (err) {
            console.error("Error saving timetable:", err);
            setMessage("Failed to save timetable. See console for details.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={styles.container}>
            <h2 className={styles.title}>Add Timetable</h2>

            <div className={styles.filters}>
                <label>
                    Department
                    <select
                        value={department}
                        onChange={(e) => setDepartment(e.target.value)}
                        className={styles.select}
                    >
                        {DEPARTMENTS.map((d) => (
                            <option key={d.value} value={d.value}>
                                {d.label}
                            </option>
                        ))}
                    </select>
                </label>

                <label>
                    Program
                    <select
                        value={program}
                        onChange={(e) => setProgram(e.target.value)}
                        className={styles.select}
                    >
                        {PROGRAMS.map((p) => (
                            <option key={p.value} value={p.value}>
                                {p.label}
                            </option>
                        ))}
                    </select>
                </label>

                <label>
                    Semester
                    <select
                        value={semester}
                        onChange={(e) => setSemester(e.target.value)}
                        className={styles.select}
                    >
                        {[...Array(8)].map((_, i) => (
                            <option key={i + 1} value={i + 1}>
                                {i + 1}
                            </option>
                        ))}
                    </select>
                </label>

                <label>
                    Section
                    <select
                        value={section}
                        onChange={(e) => setSection(e.target.value)}
                        className={styles.select}
                    >
                        {["1", "2"].map((s) => (
                            <option key={s} value={s}>
                                {s}
                            </option>
                        ))}
                    </select>
                </label>
            </div>

            <div className={styles.tableWrapper}>
                <table className={styles.timetable}>
                    <thead>
                    <tr>
                        <th className={styles.slotHead}>-</th>
                        <th className={styles.slotHead}>Slot</th>
                        {DAYS.map((d) => (
                            <th key={d}>{d}</th>
                        ))}
                    </tr>
                    </thead>
                    <tbody>
                    {slots.map((slot, si) => (
                        <React.Fragment key={si}>
                            {/* Subject row */}
                            <tr className={styles.rowLabel}>
                                <td className={styles.rowType}>Subject</td>
                                <td className={styles.slotLabel}>{slot.label}</td>
                                {DAYS.map((d) => (
                                    <td key={d}>
                                        <input
                                            className={styles.cellInput}
                                            placeholder="Subject"
                                            value={slot.days[d].subject}
                                            onChange={(e) =>
                                                handleCellChange(si, d, "subject", e.target.value)
                                            }
                                        />
                                    </td>
                                ))}
                            </tr>

                            {/* Room row */}
                            <tr>
                                <td className={styles.rowType}>Room</td>
                                <td className={styles.slotLabel}></td>
                                {DAYS.map((d) => (
                                    <td key={d}>
                                        <input
                                            className={styles.cellInput}
                                            placeholder="Room"
                                            value={slot.days[d].room}
                                            onChange={(e) =>
                                                handleCellChange(si, d, "room", e.target.value)
                                            }
                                        />
                                    </td>
                                ))}
                            </tr>

                            {/* Teacher row */}
                            <tr>
                                <td className={styles.rowType}>Teacher</td>
                                <td className={styles.slotLabel}>
                                    <div className={styles.slotControls}>
                                        <button
                                            type="button"
                                            className={styles.smallBtn}
                                            onClick={() => removeSlot(si)}
                                            disabled={slots.length <= 1}
                                            title="Remove slot"
                                        >
                                            −
                                        </button>
                                        <span className={styles.slotIndex}>{si + 1}</span>
                                    </div>
                                </td>
                                {DAYS.map((d) => (
                                    <td key={d}>
                                        <input
                                            className={styles.cellInput}
                                            placeholder="Teacher"
                                            value={slot.days[d].teacher}
                                            onChange={(e) =>
                                                handleCellChange(si, d, "teacher", e.target.value)
                                            }
                                        />
                                    </td>
                                ))}
                            </tr>
                        </React.Fragment>
                    ))}
                    </tbody>
                </table>
            </div>

            <div className={styles.controls}>
                <button
                    type="button"
                    className={styles.addSlotBtn}
                    onClick={addSlot}
                >
                    + Add Slot
                </button>

                <button
                    type="button"
                    className={styles.saveBtn}
                    onClick={handleSave}
                    disabled={loading}
                >
                    {loading ? "Saving..." : "Save Timetable"}
                </button>
            </div>

            {message && <p className={styles.message}>{message}</p>}
        </div>
    );
}
